import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { AxiosRequestConfig } from 'axios';

import type { ServiceRequestConfig } from '~/services/createService';

import { eviSearch } from '~/services/serviceInstances';

export type EvisearchBaseQueryArg = {
  url: string;
  method?: AxiosRequestConfig['method'];
  body?: unknown;
  params?: Record<string, unknown>;
  rtkQueryOptions?: ServiceRequestConfig;
};

const normalizeUrl = (rawUrl: string) => {
  const withoutApiPrefix = rawUrl.replace(/^\/?api\/v1/, '');
  return withoutApiPrefix.startsWith('/')
    ? withoutApiPrefix
    : `/${withoutApiPrefix}`;
};

export const evisearchBaseQuery: BaseQueryFn<
  EvisearchBaseQueryArg,
  unknown,
  unknown
> = async ({ url, method = 'get', body, params, rtkQueryOptions }) => {
  const path = normalizeUrl(url);
  // Unlike the esign / unified-fields baseQuery, evisearch does NOT default
  // `showErrorToast` to true: the legacy hand-written methods surfaced their
  // own errors and never triggered an automatic toast. Callers opt in via
  // `rtkQueryOptions`.
  const config = { ...rtkQueryOptions, params } as ServiceRequestConfig;

  try {
    let result: unknown;
    const m = String(method).toLowerCase();
    if (m === 'get') {
      result = await eviSearch.get(path, config);
    } else if (m === 'post') {
      result = await eviSearch.post(path, body, config);
    } else if (m === 'patch') {
      result = await eviSearch.patch(path, body, config);
    } else if (m === 'put') {
      result = await eviSearch.put(path, body, config);
    } else if (m === 'delete') {
      result = await eviSearch.remove(path, { ...config, data: body });
    } else {
      return { error: { status: 400, data: `Unsupported method: ${method}` } };
    }
    return { data: result };
  } catch (e) {
    // Preserve the raw AxiosError so legacy consumers that read
    // `error.response.status` / `error.response.data.detail` keep working
    // unchanged. This mirrors the old `createQueryFn` behavior, which surfaced
    // the raw thrown error rather than a flattened `{ status, data }` object.
    return { error: e };
  }
};
